import { useState } from 'react';

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6at5gua728k8ctu3i9g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gray-900 rounded-3xl p-12 lg:p-16 relative overflow-hidden">
          <div className="absolute bottom-0 left-0 right-0 text-center">
            <div className="text-[120px] lg:text-[180px] font-bold text-gray-800/30 leading-none select-none">
              SAGRO
            </div>
          </div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-4 gap-12">
            <div className="lg:col-span-1">
              <h2 className="text-4xl font-bold text-white mb-6">
                Get in Touch
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                Have questions about our iOS applications? We're here to help you enhance your mobile security and device performance.
              </p>
            </div>

            <div className="lg:col-span-1">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <i className="ri-map-pin-line text-lg"></i>
                Address
              </h3>
              <div className="space-y-2 text-gray-400 text-sm">
                <p>STUPSKA BB, ILIDZA</p>
                <p>City Ilidza</p>
                <p>Zip 71210</p>
                <p>Country Bosnia &amp; Herzegovina</p>
              </div>
            </div>

            <div className="lg:col-span-1">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <i className="ri-contacts-line text-lg"></i>
                Contact
              </h3>
              <div className="space-y-3 text-gray-400 text-sm">
                <a 
                  href="mailto:help@sagrodoo.com" 
                  className="flex items-center gap-2 hover:text-white transition-colors"
                >
                  <i className="ri-mail-line"></i>
                  help@sagrodoo.com
                </a>
                <a 
                  href="tel:+14243952240" 
                  className="flex items-center gap-2 hover:text-white transition-colors"
                >
                  <i className="ri-phone-line"></i>
                  +1 4243952240
                </a>
              </div>
            </div>

            <div className="lg:col-span-1">
              <h3 className="text-white font-semibold mb-4">Connect</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                We're committed to delivering high-quality iOS applications that prioritize user security, privacy, and device performance optimization.
              </p>
            </div>
          </div>

          <div className="relative z-10 mt-16 pt-12 border-t border-gray-800">
            <h3 className="text-2xl font-bold text-white mb-8">Send us a message</h3>
            
            <form 
              id="contact-form" 
              data-readdy-form 
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="How can we help?"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 transition-colors resize-none"
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <p className="mt-4 text-green-400 text-sm">
                    Thank you! Your message has been sent successfully.
                  </p>
                )}

                {submitStatus === 'error' && (
                  <p className="mt-4 text-red-400 text-sm">
                    Sorry, there was an error sending your message. Please try again.
                  </p>
                )}
              </div>
            </form>
          </div>

          <div className="relative z-10 mt-12 pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-500 text-sm">
              © 2025 SAGRO D.O.O. All rights reserved. | 
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="ml-1 hover:text-gray-300 transition-colors"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
